import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, Select, Textarea, VStack, HStack,
  useToast, Text, Box, Badge, SimpleGrid, Tabs, TabList, Tab, TabPanels, TabPanel,
  RadioGroup, Radio, Stack, InputGroup, InputLeftElement, Flex, Heading,
  FormErrorMessage, Divider,
} from '@chakra-ui/react';
import { SearchIcon, CheckCircleIcon, InfoIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { TramiteOP, TipoTramiteOP, TipoDocumentoOP, crearTramiteOP, actualizarTramiteOP } from '../../../../api/opTramiteApi';

const ESTATUS_COLORS: Record<string, string> = {
  REGISTRADO: 'blue', EN_PROCESO: 'yellow', EN_REVISION: 'orange',
  PROCEDENTE: 'green', NO_PROCEDENTE: 'red', CONCLUIDO: 'gray', CANCELADO: 'red',
};

const DOC_TYPE_COLOR: Record<string, string> = {
  LICENCIA: 'green', PERMISO: 'blue', CONSTANCIA: 'purple', DICTAMEN: 'orange',
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tramite?: TramiteOP | null;
  tiposTramite: TipoTramiteOP[];
  tiposDocumento: TipoDocumentoOP[];
  modo: 'new' | 'edit' | 'view';
  onSuccess: () => void;
}

const EMPTY_FORM = {
  tipoPersona: 'FISICA',
  idTipoTramite: '',
  tipoDocumento: '',
  nombreSolicitante: '',
  primerApellido: '',
  segundoApellido: '',
  razonSocial: '',
  representanteLegal: '',
  rfc: '',
  curp: '',
  telefono: '',
  celular: '',
  correo: '',
  calleSolicitante: '',
  numeroExteriorSol: '',
  numeroInteriorSol: '',
  coloniaSolicitante: '',
  municipioDomicilio: '',
  estadoDomicilio: '',
  codigoPostal: '',
  nomenclatura: '',
  direccion: '',
  observaciones: '',
  origen: 'PRESENCIAL',
  expedienteFisico: '',
  fechaSolicitud: new Date().toISOString().split('T')[0],
};

// ── Tab label with optional error badge ────────────────────────────────────────
const TabLabel: React.FC<{ label: string; errCount: number; active?: boolean }> = ({ label, errCount }) => (
  <Flex align="center" gap={1.5} position="relative">
    <Text>{label}</Text>
    {errCount > 0 && (
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        w="16px"
        h="16px"
        bg="red.500"
        color="white"
        borderRadius="full"
        fontSize="9px"
        fontWeight="bold"
        lineHeight="1"
        flexShrink={0}
      >
        {errCount}
      </Box>
    )}
  </Flex>
);

// ── Main component ─────────────────────────────────────────────────────────────
const ModalTramiteOP: React.FC<Props> = ({
  isOpen, onClose, tramite, tiposTramite, tiposDocumento, modo, onSuccess,
}) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [tipoSearch, setTipoSearch] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const toast = useToast();
  const readOnly = modo === 'view';

  useEffect(() => {
    if (isOpen) {
      setTabIndex(0);
      setTipoSearch('');
      setSubmitAttempted(false);
    }
    if (tramite) {
      setForm({
        tipoPersona: tramite.tipoPersona || 'FISICA',
        idTipoTramite: String(tramite.idTipoTramite),
        tipoDocumento: tramite.tipoDocumento || '',
        nombreSolicitante: tramite.nombreSolicitante,
        primerApellido: tramite.primerApellido || '',
        segundoApellido: tramite.segundoApellido || '',
        razonSocial: tramite.razonSocial || '',
        representanteLegal: tramite.representanteLegal || '',
        rfc: tramite.rfc || '',
        curp: tramite.curp || '',
        telefono: tramite.telefono,
        celular: tramite.celular || '',
        correo: tramite.correo,
        calleSolicitante: tramite.calleSolicitante || '',
        numeroExteriorSol: tramite.numeroExteriorSol || '',
        numeroInteriorSol: tramite.numeroInteriorSol || '',
        coloniaSolicitante: tramite.coloniaSolicitante || '',
        municipioDomicilio: tramite.municipioDomicilio || '',
        estadoDomicilio: tramite.estadoDomicilio || '',
        codigoPostal: tramite.codigoPostal || '',
        nomenclatura: tramite.nomenclatura || '',
        direccion: tramite.direccion || '',
        observaciones: tramite.observaciones || '',
        origen: tramite.origen,
        expedienteFisico: tramite.expedienteFisico || '',
        fechaSolicitud: tramite.fechaSolicitud?.split('T')[0] || new Date().toISOString().split('T')[0],
      });
    } else {
      setForm({ ...EMPTY_FORM });
    }
  }, [tramite, isOpen]);

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  const tipoSeleccionado = tiposTramite.find(t => String(t.id) === form.idTipoTramite);
  const solicitanteDisplay = tramite?.tipoPersona === 'MORAL'
    ? (tramite.razonSocial || tramite.nombreSolicitante)
    : [tramite?.nombreSolicitante, tramite?.primerApellido, tramite?.segundoApellido].filter(Boolean).join(' ');
  const documentos = tramite?.documentos || [];
  const requisitos = tramite?.tipoTramite?.requisitos || tipoSeleccionado?.requisitos || [];
  const obligatorios = requisitos.filter(r => r.obligatorio);
  const opcionales = requisitos.filter(r => !r.obligatorio);
  const docsCompletos = documentos.filter(d => d.estatus === 'COMPLETO').length;
  const docsPendientes = documentos.filter(d => d.estatus !== 'COMPLETO').length;
  const fechaRegistro = tramite?.fechaRegistro ? new Date(tramite.fechaRegistro).toLocaleString('es-MX') : '—';

  // Filtered tipo list for picker
  const tiposFiltrados = useMemo(() => {
    if (!tipoSearch.trim()) return tiposTramite;
    const q = tipoSearch.toLowerCase();
    return tiposTramite.filter(t =>
      t.nombre.toLowerCase().includes(q) ||
      (t.codigoDocumento?.toLowerCase() || '').includes(q) ||
      (t.tipoDocumentoSalida?.toLowerCase() || '').includes(q)
    );
  }, [tiposTramite, tipoSearch]);

  // Per-field validation
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.idTipoTramite) e.idTipoTramite = 'Seleccione un tipo de trámite';
    if (form.tipoPersona === 'MORAL') {
      if (!form.razonSocial.trim()) e.razonSocial = 'Requerido';
    } else {
      if (!form.nombreSolicitante.trim()) e.nombreSolicitante = 'Requerido';
    }
    if (!form.telefono.trim()) e.telefono = 'Requerido';
    if (!form.correo.trim()) e.correo = 'Requerido';
    if (form.rfc.trim()) {
      const rfcReg = form.tipoPersona === 'FISICA'
        ? /^[A-ZÑ&]{4}\d{6}[A-Z\d]{3}$/
        : /^[A-ZÑ&]{3}\d{6}[A-Z\d]{3}$/;
      if (!rfcReg.test(form.rfc)) e.rfc = 'Formato inválido';
    }
    return e;
  };

  // Count missing required fields per tab (only shown after submit attempt)
  const tabErrorCount = (tabIdx: number): number => {
    if (!submitAttempted) return 0;
    const e = validate();
    if (tabIdx === 0) return ['idTipoTramite'].filter(k => e[k]).length;
    if (tabIdx === 1) {
      const keys = form.tipoPersona === 'MORAL'
        ? ['razonSocial', 'telefono', 'correo', 'rfc']
        : ['nombreSolicitante', 'telefono', 'correo', 'rfc'];
      return keys.filter(k => e[k]).length;
    }
    return 0;
  };

  const handleTipoChange = (idTipo: string) => {
    const tipo = tiposTramite.find(t => String(t.id) === idTipo);
    setForm(prev => ({
      ...prev,
      idTipoTramite: idTipo,
      tipoDocumento: tipo?.tipoDocumentoSalida || prev.tipoDocumento,
    }));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      // Jump to first tab with errors
      if (errs.idTipoTramite) setTabIndex(0);
      else setTabIndex(1);
      toast({ title: 'Faltan campos obligatorios', status: 'error', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      const payload: any = { ...form, idTipoTramite: parseInt(form.idTipoTramite) };
      if (modo === 'edit' && tramite) {
        await actualizarTramiteOP(tramite.id, payload);
        toast({ title: 'Trámite actualizado', status: 'success', duration: 3000 });
      } else {
        await crearTramiteOP(payload);
        toast({ title: 'Trámite registrado correctamente', status: 'success', duration: 3000 });
      }
      onSuccess();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || err.message || 'Error al guardar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const errors = submitAttempted ? validate() : {};

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          {modo === 'view' && tramite ? `Trámite ${tramite.folio}` : modo === 'edit' ? 'Editar Trámite' : 'Nuevo Trámite de Obras Públicas'}
        </ModalHeader>
        <ModalCloseButton color="white" />

        <ModalBody pt={4}>
          {/* ── VIEW MODE: expediente summary card ─────────────────────── */}
          {modo === 'view' && tramite && (
            <Box mb={5} overflow="hidden" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
              <Box bgGradient="linear(to-r, #7b1f35, #5b182b)" color="white" px={5} py={4}>
                <Flex justify="space-between" gap={4} align={{ base: 'start', md: 'center' }} flexWrap="wrap">
                  <Box>
                    <Text fontSize="xs" opacity={0.82} textTransform="uppercase" letterSpacing="0.08em">
                      Expediente de Obras Públicas
                    </Text>
                    <Heading size="md" mt={1}>{tramite.folio}</Heading>
                    <Text fontSize="sm" opacity={0.9} mt={1}>{tramite.tipoTramite?.nombre || 'Trámite sin clasificación'}</Text>
                  </Box>
                  <VStack align={{ base: 'start', md: 'end' }} spacing={1}>
                    <Badge colorScheme={ESTATUS_COLORS[tramite.estatus] || 'gray'} fontSize="sm" px={3} py={1} borderRadius="full">
                      {tramite.estatus}
                    </Badge>
                    <Text fontSize="xs" opacity={0.85}>Registrado: {fechaRegistro}</Text>
                  </VStack>
                </Flex>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={0} borderBottomWidth="1px" borderColor="gray.100">
                <Box p={4} borderRightWidth={{ md: '1px' }} borderColor="gray.100">
                  <Text fontSize="xs" color="gray.500" mb={1}>Solicitante</Text>
                  <Text fontWeight="bold" color="gray.800" noOfLines={2}>{solicitanteDisplay || '—'}</Text>
                  <Badge mt={2} colorScheme={tramite.tipoPersona === 'MORAL' ? 'purple' : 'blue'} variant="subtle">
                    {tramite.tipoPersona === 'MORAL' ? 'Persona moral' : 'Persona física'}
                  </Badge>
                </Box>
                <Box p={4} borderRightWidth={{ md: '1px' }} borderColor="gray.100">
                  <Text fontSize="xs" color="gray.500" mb={1}>Contacto</Text>
                  <Text fontSize="sm" fontWeight="semibold">{tramite.telefono || '—'}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{tramite.correo || 'Sin correo'}</Text>
                </Box>
                <Box p={4} borderRightWidth={{ md: '1px' }} borderColor="gray.100">
                  <Text fontSize="xs" color="gray.500" mb={1}>Expediente</Text>
                  <Text fontSize="sm" fontWeight="semibold">{tramite.expediente?.numeroExpediente || 'Sin asignar'}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{tramite.expedienteFisico || 'Sin ubicación física'}</Text>
                </Box>
                <Box p={4}>
                  <Text fontSize="xs" color="gray.500" mb={1}>Documentación</Text>
                  <Text fontSize="sm" fontWeight="semibold">{documentos.length} archivo(s)</Text>
                  <Text fontSize="xs" color={docsPendientes > 0 ? 'orange.600' : 'green.600'}>
                    {docsCompletos} completos · {docsPendientes} por revisar
                  </Text>
                </Box>
              </SimpleGrid>

              <Box p={4} bg="gray.50">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <Box bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" color="gray.500">Documento de salida</Text>
                    <HStack mt={1} spacing={2}>
                      <Badge colorScheme={DOC_TYPE_COLOR[tramite.tipoDocumento || ''] || 'gray'} variant="subtle">
                        {tramite.tipoDocumento || '—'}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">{tipoSeleccionado?.codigoDocumento || ''}</Text>
                    </HStack>
                  </Box>
                  <Box bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" color="gray.500">Vigencia del documento</Text>
                    <Text mt={1} fontWeight="bold" fontSize="sm">
                      {tipoSeleccionado?.vigenciaMeses === 0 ? 'Sin vencimiento' : tipoSeleccionado?.vigenciaMeses ? `${tipoSeleccionado.vigenciaMeses} mes(es)` : '—'}
                    </Text>
                  </Box>
                  <Box bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.100">
                    <Text fontSize="xs" color="gray.500">Origen</Text>
                    <Badge mt={1} colorScheme={tramite.origen === 'ONLINE' ? 'green' : 'gray'}>{tramite.origen}</Badge>
                  </Box>
                </SimpleGrid>

                {documentos.length > 0 && (
                  <Box mt={4}>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Documentos recibidos</Text>
                    <VStack align="stretch" spacing={2}>
                      {documentos.slice(0, 4).map(doc => (
                        <Flex key={doc.id} bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="md" px={3} py={2}
                          justify="space-between" align="center" gap={3}>
                          <Box minW={0}>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{doc.nombreArchivo}</Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>{doc.descripcion || doc.tipoArchivo}</Text>
                          </Box>
                          <Badge colorScheme={doc.estatus === 'COMPLETO' ? 'green' : doc.estatus === 'RECHAZADO' ? 'red' : 'orange'} flexShrink={0}>
                            {doc.estatus}
                          </Badge>
                        </Flex>
                      ))}
                      {documentos.length > 4 && (
                        <Text fontSize="xs" color="gray.500">+{documentos.length - 4} documento(s) más en la sección de Documentos.</Text>
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── TABS ────────────────────────────────────────────────────── */}
          <Tabs variant="enclosed" colorScheme="red" index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab><TabLabel label="Tipo de Trámite" errCount={tabErrorCount(0)} /></Tab>
              <Tab><TabLabel label="Solicitante" errCount={tabErrorCount(1)} /></Tab>
              <Tab><TabLabel label="Domicilio" errCount={tabErrorCount(2)} /></Tab>
              <Tab><TabLabel label="Predio y Observaciones" errCount={tabErrorCount(3)} /></Tab>
            </TabList>

            <TabPanels>
              {/* ── TAB 1: TIPO ──────────────────────────────────────────── */}
              <TabPanel>
                <VStack spacing={4} align="stretch">

                  {/* Picker de tipo: card list en modo nuevo, select en edición */}
                  {modo === 'new' ? (
                    <FormControl isRequired isInvalid={!!errors.idTipoTramite}>
                      <FormLabel>Tipo de Trámite</FormLabel>
                      <InputGroup size="sm" mb={2}>
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Buscar por nombre, código o tipo de documento..."
                          value={tipoSearch}
                          onChange={e => setTipoSearch(e.target.value)}
                          bg="gray.50"
                          borderRadius="md"
                        />
                      </InputGroup>
                      <Box
                        maxH="300px"
                        overflowY="auto"
                        borderWidth="1px"
                        borderColor={errors.idTipoTramite ? 'red.300' : 'gray.200'}
                        borderRadius="lg"
                        bg="white"
                      >
                        {tiposFiltrados.length === 0 ? (
                          <Box p={6} textAlign="center">
                            <Text color="gray.400" fontSize="sm">Sin resultados para "{tipoSearch}"</Text>
                          </Box>
                        ) : tiposFiltrados.map((tipo, idx) => {
                          const selected = String(tipo.id) === form.idTipoTramite;
                          return (
                            <Flex
                              key={tipo.id}
                              px={4} py={3}
                              cursor="pointer"
                              bg={selected ? '#5b182b' : 'white'}
                              color={selected ? 'white' : 'gray.700'}
                              _hover={{ bg: selected ? '#8b1f33' : 'red.50' }}
                              borderBottomWidth={idx < tiposFiltrados.length - 1 ? '1px' : '0'}
                              borderColor="gray.100"
                              align="center"
                              justify="space-between"
                              gap={3}
                              transition="background 0.12s"
                              onClick={() => handleTipoChange(String(tipo.id))}
                            >
                              <Box minW={0} flex={1}>
                                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                                  {tipo.nombre}
                                </Text>
                                {tipo.costoDescripcion && (
                                  <Text fontSize="xs" opacity={selected ? 0.8 : 0.6} noOfLines={1} mt={0.5}>
                                    {tipo.costoDescripcion}
                                  </Text>
                                )}
                              </Box>
                              <HStack spacing={1.5} flexShrink={0}>
                                {tipo.tipoDocumentoSalida && (
                                  <Badge
                                    fontSize="2xs"
                                    colorScheme={selected ? undefined : DOC_TYPE_COLOR[tipo.tipoDocumentoSalida] || 'gray'}
                                    bg={selected ? 'whiteAlpha.300' : undefined}
                                    color={selected ? 'white' : undefined}
                                    variant={selected ? undefined : 'subtle'}
                                  >
                                    {tipo.tipoDocumentoSalida}
                                  </Badge>
                                )}
                                {tipo.vigenciaMeses !== undefined && (
                                  <Badge
                                    fontSize="2xs"
                                    colorScheme={selected ? undefined : 'gray'}
                                    bg={selected ? 'whiteAlpha.200' : undefined}
                                    color={selected ? 'white' : undefined}
                                    variant={selected ? undefined : 'subtle'}
                                  >
                                    {tipo.vigenciaMeses === 0 ? 'Sin venc.' : `${tipo.vigenciaMeses}m`}
                                  </Badge>
                                )}
                              </HStack>
                            </Flex>
                          );
                        })}
                      </Box>
                      <FormErrorMessage>{errors.idTipoTramite}</FormErrorMessage>
                    </FormControl>
                  ) : (
                    <FormControl isRequired>
                      <FormLabel>Tipo de Trámite</FormLabel>
                      <Select
                        value={form.idTipoTramite}
                        onChange={e => handleTipoChange(e.target.value)}
                        isDisabled={readOnly || modo === 'edit'}
                      >
                        <option value="">Seleccione...</option>
                        {tiposTramite.map(t => (
                          <option key={t.id} value={t.id}>{t.nombre}</option>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Tipo seleccionado: banner de detalles */}
                  {tipoSeleccionado && (
                    <Box borderRadius="lg" overflow="hidden" borderWidth="1px" borderColor="red.100">
                      <Box bg="#5b182b" px={4} py={3} color="white">
                        <Flex justify="space-between" align="start" gap={3} flexWrap="wrap">
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">{tipoSeleccionado.nombre}</Text>
                            {tipoSeleccionado.costoDescripcion && (
                              <Text fontSize="xs" opacity={0.85} mt={0.5}>{tipoSeleccionado.costoDescripcion}</Text>
                            )}
                          </Box>
                          <HStack spacing={1.5} flexShrink={0} flexWrap="wrap" justify="flex-end">
                            {tipoSeleccionado.tipoDocumentoSalida && (
                              <Badge bg="whiteAlpha.300" color="white" fontSize="xs">
                                {tipoSeleccionado.tipoDocumentoSalida}
                              </Badge>
                            )}
                            {tipoSeleccionado.codigoDocumento && (
                              <Badge bg="whiteAlpha.300" color="white" fontSize="xs">
                                {tipoSeleccionado.codigoDocumento}
                              </Badge>
                            )}
                            {tipoSeleccionado.vigenciaMeses !== undefined && (
                              <Badge bg="whiteAlpha.200" color="white" fontSize="xs">
                                {tipoSeleccionado.vigenciaMeses === 0 ? 'Sin vencimiento' : `${tipoSeleccionado.vigenciaMeses} mes${tipoSeleccionado.vigenciaMeses !== 1 ? 'es' : ''}`}
                              </Badge>
                            )}
                          </HStack>
                        </Flex>
                        {tipoSeleccionado.codigoDocumento && (
                          <Text fontSize="2xs" opacity={0.65} mt={1}>
                            Nomenclatura: PMT-DOPDU-&#123;mes&#125;/{tipoSeleccionado.codigoDocumento}-&#123;seq&#125;/&#123;año&#125;
                          </Text>
                        )}
                      </Box>

                      {/* Requisitos */}
                      {requisitos.length > 0 && (
                        <Box px={4} py={3} bg="red.50">
                          <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="0.06em">
                            Requisitos del trámite
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
                            {[...obligatorios, ...opcionales]
                              .sort((a, b) => a.orden - b.orden)
                              .map(req => (
                                <HStack key={req.id} align="start" spacing={2} py={1}>
                                  <Box mt="2px" flexShrink={0}>
                                    {req.obligatorio
                                      ? <CheckCircleIcon color="green.500" boxSize={3.5} />
                                      : <InfoIcon color="gray.400" boxSize={3.5} />
                                    }
                                  </Box>
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight={req.obligatorio ? 'semibold' : 'normal'}
                                      color={req.obligatorio ? 'gray.800' : 'gray.500'}
                                      lineHeight="1.4"
                                    >
                                      {req.nombreRequisito}
                                      {req.obligatorio && <Text as="span" color="red.500"> *</Text>}
                                    </Text>
                                    {req.descripcion && (
                                      <Text fontSize="2xs" color="gray.400">{req.descripcion}</Text>
                                    )}
                                  </Box>
                                </HStack>
                              ))}
                          </SimpleGrid>
                          <Text fontSize="2xs" color="gray.400" mt={2}>
                            * Obligatorio · {obligatorios.length} obligatorio{obligatorios.length !== 1 ? 's' : ''} · {opcionales.length} opcional{opcionales.length !== 1 ? 'es' : ''}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Fecha de Solicitud</FormLabel>
                      <Input
                        type="date"
                        value={form.fechaSolicitud}
                        onChange={e => set('fechaSolicitud', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Origen del Trámite</FormLabel>
                      <Select
                        value={form.origen}
                        onChange={e => set('origen', e.target.value)}
                        isDisabled={readOnly}
                      >
                        <option value="PRESENCIAL">Presencial</option>
                        <option value="ONLINE">En línea</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Tipo de Documento de Salida</FormLabel>
                    <Select
                      value={form.tipoDocumento}
                      onChange={e => set('tipoDocumento', e.target.value)}
                      isDisabled={readOnly}
                    >
                      <option value="">Seleccione...</option>
                      {tiposDocumento.map(t => (
                        <option key={t.id} value={t.codigo}>{t.descripcion}</option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Ubicación del Expediente Físico</FormLabel>
                    <Input
                      placeholder="Ej. Archivo / Gaveta 3 / Carpeta 12"
                      value={form.expedienteFisico}
                      onChange={e => set('expedienteFisico', e.target.value)}
                      isReadOnly={readOnly}
                    />
                  </FormControl>
                </VStack>
              </TabPanel>

              {/* ── TAB 2: SOLICITANTE ───────────────────────────────────── */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Tipo de Persona</FormLabel>
                    <RadioGroup
                      value={form.tipoPersona}
                      onChange={v => set('tipoPersona', v)}
                      isDisabled={readOnly}
                    >
                      <Stack direction="row">
                        <Radio value="FISICA">Persona Física</Radio>
                        <Radio value="MORAL">Persona Moral</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {form.tipoPersona === 'MORAL' ? (
                    <>
                      <FormControl isRequired isInvalid={!!errors.razonSocial}>
                        <FormLabel>Razón Social</FormLabel>
                        <Input
                          value={form.razonSocial}
                          onChange={e => set('razonSocial', e.target.value)}
                          isReadOnly={readOnly}
                          placeholder="Nombre de la empresa o persona moral"
                        />
                        <FormErrorMessage>{errors.razonSocial}</FormErrorMessage>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Representante Legal</FormLabel>
                        <Input
                          value={form.representanteLegal}
                          onChange={e => set('representanteLegal', e.target.value)}
                          isReadOnly={readOnly}
                        />
                      </FormControl>
                    </>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <FormControl isRequired isInvalid={!!errors.nombreSolicitante}>
                        <FormLabel>Nombre(s)</FormLabel>
                        <Input
                          value={form.nombreSolicitante}
                          onChange={e => set('nombreSolicitante', e.target.value)}
                          isReadOnly={readOnly}
                        />
                        <FormErrorMessage>{errors.nombreSolicitante}</FormErrorMessage>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Primer Apellido</FormLabel>
                        <Input
                          value={form.primerApellido}
                          onChange={e => set('primerApellido', e.target.value)}
                          isReadOnly={readOnly}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Segundo Apellido</FormLabel>
                        <Input
                          value={form.segundoApellido}
                          onChange={e => set('segundoApellido', e.target.value)}
                          isReadOnly={readOnly}
                        />
                      </FormControl>
                    </SimpleGrid>
                  )}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired isInvalid={!!errors.rfc}>
                      <FormLabel>RFC</FormLabel>
                      <Input
                        value={form.rfc}
                        onChange={e => set('rfc', e.target.value.toUpperCase())}
                        isReadOnly={readOnly}
                        maxLength={13}
                        placeholder={form.tipoPersona === 'FISICA' ? 'AAAA######AAA (13)' : 'AAA######AAA (12)'}
                      />
                      <FormErrorMessage>{errors.rfc}</FormErrorMessage>
                    </FormControl>
                    {form.tipoPersona === 'FISICA' && (
                      <FormControl>
                        <FormLabel>CURP</FormLabel>
                        <Input
                          value={form.curp}
                          onChange={e => set('curp', e.target.value.toUpperCase())}
                          isReadOnly={readOnly}
                          maxLength={18}
                          placeholder="18 caracteres"
                        />
                      </FormControl>
                    )}
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isRequired isInvalid={!!errors.telefono}>
                      <FormLabel>Teléfono</FormLabel>
                      <Input
                        value={form.telefono}
                        onChange={e => set('telefono', e.target.value)}
                        isReadOnly={readOnly}
                        placeholder="10 dígitos"
                      />
                      <FormErrorMessage>{errors.telefono}</FormErrorMessage>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Celular</FormLabel>
                      <Input
                        value={form.celular}
                        onChange={e => set('celular', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.correo}>
                      <FormLabel>Correo Electrónico</FormLabel>
                      <Input
                        type="email"
                        value={form.correo}
                        onChange={e => set('correo', e.target.value)}
                        isReadOnly={readOnly}
                      />
                      <FormErrorMessage>{errors.correo}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* ── TAB 3: DOMICILIO ─────────────────────────────────────── */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Text fontWeight="bold" color="gray.600">Domicilio del Solicitante</Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl gridColumn={{ md: 'span 2' }}>
                      <FormLabel>Calle</FormLabel>
                      <Input
                        value={form.calleSolicitante}
                        onChange={e => set('calleSolicitante', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>No. Exterior</FormLabel>
                      <Input
                        value={form.numeroExteriorSol}
                        onChange={e => set('numeroExteriorSol', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel>No. Interior</FormLabel>
                      <Input
                        value={form.numeroInteriorSol}
                        onChange={e => set('numeroInteriorSol', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Colonia</FormLabel>
                      <Input
                        value={form.coloniaSolicitante}
                        onChange={e => set('coloniaSolicitante', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>C.P.</FormLabel>
                      <Input
                        value={form.codigoPostal}
                        onChange={e => set('codigoPostal', e.target.value)}
                        isReadOnly={readOnly}
                        maxLength={5}
                      />
                    </FormControl>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Municipio</FormLabel>
                      <Input
                        value={form.municipioDomicilio}
                        onChange={e => set('municipioDomicilio', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Estado</FormLabel>
                      <Input
                        value={form.estadoDomicilio}
                        onChange={e => set('estadoDomicilio', e.target.value)}
                        isReadOnly={readOnly}
                      />
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* ── TAB 4: PREDIO ────────────────────────────────────────── */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Dirección del Predio / Lugar de la Obra</FormLabel>
                    <Input
                      value={form.direccion}
                      onChange={e => set('direccion', e.target.value)}
                      isReadOnly={readOnly}
                      placeholder="Calle, No., Colonia, Municipio"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Nomenclatura / Descripción del Predio</FormLabel>
                    <Textarea
                      value={form.nomenclatura}
                      onChange={e => set('nomenclatura', e.target.value)}
                      isReadOnly={readOnly}
                      rows={3}
                      placeholder="Lote, manzana, fraccionamiento, clave catastral, etc."
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Observaciones</FormLabel>
                    <Textarea
                      value={form.observaciones}
                      onChange={e => set('observaciones', e.target.value)}
                      isReadOnly={readOnly}
                      rows={4}
                      placeholder="Observaciones generales del trámite"
                    />
                  </FormControl>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter borderTop="1px solid #eee">
          <Button variant="ghost" mr={3} onClick={onClose}>
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!readOnly && (
            <Button bg="#5b182b" color="white" _hover={{ bg: '#8b1f33' }} onClick={handleSubmit} isLoading={loading}>
              {modo === 'edit' ? 'Guardar Cambios' : 'Registrar Trámite'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalTramiteOP;
